import { useEffect, useState, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Receipt, Boxes, BarChart3,
  Home, Sun, Moon, Bell, Search, LogOut, Menu, X, ChevronRight, AlertTriangle, Clock, ChevronDown, Sparkles,
} from "lucide-react";
import { NAV } from "./nav.config";
import { BRAND } from "@/lib/brand";
import { useCompany } from "@/lib/useCompany";
import AskAIDrawer from "./AskAIDrawer";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const STORAGE_KEY = "wl_nav_expanded_v1";

// Route helpers -------------------------------------------------------------
const parseHref = (to) => {
  const [path, query = ""] = to.split("?");
  const params = new URLSearchParams(query);
  return { path, params };
};

const isChildActive = (child, pathname, search) => {
  const { path, params } = parseHref(child.to);
  const paths = child.match || [path];
  if (!paths.some((p) => pathname === p || pathname.startsWith(p + "/"))) return false;
  // If the leaf carries query params, require every one of them to be present
  if ([...params.keys()].length === 0) return true;
  const cur = new URLSearchParams(search);
  for (const [k, v] of params) if (cur.get(k) !== v) return false;
  return true;
};

const groupHasActiveDescendant = (group, pathname, search) => {
  for (const c of group.children || []) {
    if (c.to) { if (isChildActive(c, pathname, search)) return true; }
    else if (groupHasActiveDescendant(c, pathname, search)) return true;
  }
  return false;
};

const groupHasWeakActive = (group, pathname) => {
  // Fallback: mark parent as active if pathname *starts with* any leaf path (ignoring query)
  for (const c of group.children || []) {
    if (c.to) {
      const { path } = parseHref(c.to);
      const paths = c.match || [path];
      if (paths.some((p) => pathname === p || pathname.startsWith(p + "/"))) return true;
    } else if (groupHasWeakActive(c, pathname)) return true;
  }
  return false;
};

// Recursively drop nav nodes the current user has no `view` permission for.
// A group is kept only if at least one child survives filtering.
const filterNavByPermissions = (nodes, hasPermission, role) => {
  const walk = (node) => {
    if (node.children) {
      const kids = node.children.map(walk).filter(Boolean);
      if (kids.length === 0) return null;
      // Some children survived → keep group even if its own moduleKey is denied
      return { ...node, children: kids };
    }
    // clientOnly entries (e.g. Client Portal) are hidden from staff — merged into Clients.
    if (node.clientOnly && role !== "client") return null;
    if (!node.moduleKey) return node; // legacy nav entry with no gate → keep
    return hasPermission(node.moduleKey, "view") ? node : null;
  };
  return nodes.map(walk).filter(Boolean);
};

// ---------------------------------------------------------------------------
// Level-2 (leaf inside a Level-1 group)
function Level2({ node, pathname, search, onNavigate }) {
  const active = isChildActive(node, pathname, search);
  const Icon = node.icon || ChevronRight;
  const slug = (node.to || "").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();
  return (
    <NavLink
      to={node.to}
      end={node.to === "/"}
      onClick={onNavigate}
      data-testid={`nav-${slug || "dashboard"}`}
      className={active
        ? "pl-11 pr-3 py-2 rounded-lg flex items-center gap-3 text-sm bg-primary/10 text-primary border-l-2 border-primary font-medium"
        : "pl-11 pr-3 py-2 rounded-lg flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60"}
    >
      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.6} />
      <span>{node.label}</span>
    </NavLink>
  );
}

// Level-1 group (sub-group under Finance) — contains only Level2 leaves
function Level1Group({ node, pathname, search, expanded, setExpanded, onNavigate }) {
  const isOpen = !!expanded[node.key];
  const hasActive = groupHasWeakActive(node, pathname);
  useEffect(() => {
    if (hasActive && !expanded[node.key]) setExpanded((prev) => ({ ...prev, [node.key]: true }));
     
  }, [pathname]);
  const Icon = node.icon || ChevronRight;
  const btnCls = hasActive
    ? "pl-6 pr-3 py-2 w-full rounded-lg flex items-center gap-3 text-sm text-foreground font-medium hover:bg-muted/60"
    : "pl-6 pr-3 py-2 w-full rounded-lg flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60";
  return (
    <div>
      <button
        onClick={() => setExpanded((prev) => ({ ...prev, [node.key]: !prev[node.key] }))}
        data-testid={`nav-group-${node.key}`}
        aria-expanded={isOpen}
        className={btnCls}
      >
        <Icon className="w-4 h-4 shrink-0" strokeWidth={1.6} />
        <span className="flex-1 text-left">{node.label}</span>
        <ChevronDown className={isOpen ? "w-3 h-3" : "w-3 h-3 -rotate-90"} strokeWidth={2} />
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-0.5">
          {(node.children || []).map((child) => (
            <Level2 key={child.to} node={child} pathname={pathname} search={search} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

// Level-0 group (top-level like Finance) — can hold Level1 sub-groups + Level2 leaves
function Level0Group({ node, pathname, search, expanded, setExpanded, onNavigate }) {
  const isOpen = !!expanded[node.key];
  const hasActive = groupHasWeakActive(node, pathname);
  useEffect(() => {
    if (hasActive && !expanded[node.key]) setExpanded((prev) => ({ ...prev, [node.key]: true }));
     
  }, [pathname]);
  const Icon = node.icon || ChevronRight;
  const btnCls = hasActive
    ? "px-3 py-2.5 w-full rounded-lg flex items-center gap-3 text-sm text-foreground font-medium hover:bg-muted/60"
    : "px-3 py-2.5 w-full rounded-lg flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60";
  return (
    <div>
      <button
        onClick={() => setExpanded((prev) => ({ ...prev, [node.key]: !prev[node.key] }))}
        data-testid={`nav-group-${node.key}`}
        aria-expanded={isOpen}
        className={btnCls}
      >
        <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} />
        <span className="flex-1 text-left">{node.label}</span>
        <ChevronDown className={isOpen ? "w-3.5 h-3.5" : "w-3.5 h-3.5 -rotate-90"} strokeWidth={2} />
      </button>
      {isOpen && (
        <div className="mt-0.5 ml-4 space-y-0.5 border-l border-border/40">
          {(node.children || []).map((child) => (
            child.children
              ? <Level1Group key={child.key} node={child} pathname={pathname} search={search} expanded={expanded} setExpanded={setExpanded} onNavigate={onNavigate} />
              : <Level2 key={child.to} node={child} pathname={pathname} search={search} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

// Level-0 leaf (top-level route without children)
function Level0Leaf({ node, pathname, search, onNavigate }) {
  const active = isChildActive(node, pathname, search);
  const Icon = node.icon || ChevronRight;
  const slug = (node.to || "").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();
  return (
    <NavLink
      to={node.to}
      end={node.to === "/"}
      onClick={onNavigate}
      data-testid={`nav-${slug || "dashboard"}`}
      className={active
        ? "px-3 py-2.5 rounded-lg flex items-center gap-3 text-sm bg-primary/10 text-primary border-l-2 border-primary font-medium"
        : "px-3 py-2.5 rounded-lg flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60"}
    >
      <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} />
      <span>{node.label}</span>
    </NavLink>
  );
}

export default function AppShell({ children }) {
  const { theme, toggle } = useTheme();
  const { user, logout, hasPermission } = useAuth();
  const { company } = useCompany();
  const brandName = company?.name || BRAND.name;
  const brandLogo = company?.logo_url || BRAND.logoUrl;
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [index, setIndex] = useState({ leads: [], clients: [], projects: [], employees: [], invoices: [], boqs: [] });
  const [notifOpen, setNotifOpen] = useState(false);
  const [askAiOpen, setAskAiOpen] = useState(false);
  const [notifs, setNotifs] = useState({ low_stock: [], overdue: [], pending: [] });
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch (e) { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded)); } catch (e) { /* ignore */ } }, [expanded]);

  // Compute current-page label for the breadcrumb
  const currentLabel = useMemo(() => {
    const findLeaf = (nodes) => {
      for (const n of nodes) {
        if (n.to && isChildActive(n, loc.pathname, loc.search)) return n.label;
        if (n.children) { const found = findLeaf(n.children); if (found) return found; }
      }
      return null;
    };
    return findLeaf(NAV) || "Home";
  }, [loc.pathname, loc.search]);

  // Build search index once
  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/leads").catch(() => ({ data: [] })),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get("/projects").catch(() => ({ data: [] })),
      api.get("/employees").catch(() => ({ data: [] })),
      api.get("/invoices").catch(() => ({ data: [] })),
      api.get("/boq").catch(() => ({ data: [] })),
    ]).then(([l, c, p, e, i, b]) => {
      setIndex({ leads: l.data, clients: c.data, projects: p.data, employees: e.data, invoices: i.data, boqs: b.data });
    });
  }, [user, loc.pathname]);

  // Compute notifications
  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/inventory").catch(() => ({ data: [] })),
      api.get("/invoices").catch(() => ({ data: [] })),
    ]).then(([inv, invc]) => {
      const low_stock = inv.data.filter(r => Number(r.quantity) < Number(r.reorder_level || 0)).slice(0, 8);
      const overdue = invc.data.filter(r => r.status === "overdue").slice(0, 8);
      const pending = invc.data.filter(r => r.status === "pending").slice(0, 8);
      setNotifs({ low_stock, overdue, pending });
    });
  }, [user, loc.pathname]);

  const totalNotifs = notifs.low_stock.length + notifs.overdue.length;

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const can = (m) => hasPermission(m, "view");
    const matches = [];
    if (can("crm")) index.leads.forEach(r => {
      if ((r.name||"").toLowerCase().includes(term) || (r.email||"").toLowerCase().includes(term) || (r.phone||"").toLowerCase().includes(term))
        matches.push({ type: "Lead", label: r.name, sub: r.email || r.phone || "", to: `/leads/${r.id}` });
    });
    if (can("clients")) index.clients.forEach(r => {
      if ((r.name||"").toLowerCase().includes(term) || (r.company||"").toLowerCase().includes(term) || (r.email||"").toLowerCase().includes(term))
        matches.push({ type: "Client", label: r.name, sub: r.company || r.email || "", to: `/clients` });
    });
    if (can("projects")) index.projects.forEach(r => {
      if ((r.name||"").toLowerCase().includes(term) || (r.location||"").toLowerCase().includes(term) || (r.code||"").toLowerCase().includes(term))
        matches.push({ type: "Project", label: r.name, sub: r.location || r.code || "", to: `/projects/${r.id}` });
    });
    if (can("employees")) index.employees.forEach(r => {
      if ((r.name||"").toLowerCase().includes(term) || (r.role||"").toLowerCase().includes(term) || (r.email||"").toLowerCase().includes(term))
        matches.push({ type: "Employee", label: r.name, sub: (r.role||"").replace("_"," "), to: `/employees` });
    });
    if (can("invoices")) index.invoices.forEach(r => {
      if ((r.number||"").toLowerCase().includes(term))
        matches.push({ type: "Invoice", label: r.number, sub: fmt(r.total), to: `/invoices` });
    });
    if (can("boq")) index.boqs.forEach(r => {
      if ((r.number||"").toLowerCase().includes(term) || (r.title||"").toLowerCase().includes(term))
        matches.push({ type: "BOQ", label: r.title, sub: r.number || "", to: `/boq` });
    });
    // Include quick-nav for menu items the user is allowed to see.
    const navMatches = [];
    const walk = (nodes) => {
      nodes.forEach((n) => {
        const allowed = !n.moduleKey || can(n.moduleKey);
        if (allowed && n.to && n.label.toLowerCase().includes(term)) navMatches.push({ type: "Menu", label: n.label, sub: n.to, to: n.to });
        if (n.children) walk(n.children);
      });
    };
    walk(NAV);
    return [...navMatches.slice(0, 4), ...matches].slice(0, 14);
  }, [q, index, hasPermission]);

  const go = (to) => { setQ(""); setShowResults(false); nav(to); };
  const closeMobile = () => setOpen(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {open && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      <aside data-testid="sidebar"
        className={`fixed top-0 left-0 z-50 h-screen w-64 glass border-r border-border/60 flex flex-col transform transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="h-16 flex items-center gap-2.5 px-4 border-b border-border/50">
          <img
            src={brandLogo}
            alt={brandName}
            data-testid="sidebar-logo"
            className="h-9 w-9 rounded-md object-contain bg-white p-1 ring-1 ring-black/5 shadow-sm shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm font-semibold tracking-tight truncate" data-testid="sidebar-brand-name">{brandName}</div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground truncate">Interior · ERP Studio</div>
          </div>
          <button className="lg:hidden text-muted-foreground" onClick={() => setOpen(false)} aria-label="close"><X className="w-5 h-5" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {filterNavByPermissions(NAV, hasPermission, user?.role).map((item) =>
            item.children
              ? <Level0Group key={item.key} node={item} pathname={loc.pathname} search={loc.search} expanded={expanded} setExpanded={setExpanded} onNavigate={closeMobile} />
              : <Level0Leaf key={item.to} node={item} pathname={loc.pathname} search={loc.search} onNavigate={closeMobile} />
          )}
        </nav>

        <div className="p-3 border-t border-border/50">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-accent/20 border border-border/50 flex items-center justify-center text-accent font-semibold">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground capitalize truncate">{user?.role?.replace("_", " ")}</div>
            </div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="text-muted-foreground hover:text-primary transition-colors" aria-label="logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header data-testid="topbar" className="sticky top-0 z-30 h-16 glass border-b border-border/50 flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="menu"><Menu className="w-5 h-5" /></button>

          <div className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-muted-foreground" data-testid="topbar-brand-name">{brandName}</span>
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
            <span className="font-medium">{currentLabel}</span>
          </div>

          <div className="flex-1" />

          {/* Functional Search */}
          <div className="hidden sm:block relative">
            <div className="flex items-center gap-2 h-9 px-3 rounded-lg bg-muted/50 border border-border/60 min-w-[220px] max-w-md focus-within:border-primary/60">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input
                data-testid="global-search"
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); setShowResults(true); }}
                onFocus={() => setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 200)}
                placeholder="Search projects, leads, clients, menu…"
                className="bg-transparent outline-none text-sm flex-1 placeholder:text-muted-foreground"
              />
              {q && <button onClick={() => setQ("")} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
            </div>
            {showResults && q && (
              <div data-testid="search-results" className="absolute top-full mt-1 right-0 w-[380px] max-h-[420px] overflow-auto bg-popover border border-border/60 rounded-lg shadow-xl z-50">
                {results.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">No results for &ldquo;{q}&rdquo;</div>
                ) : (
                  <div className="py-1">
                    {results.map((r, i) => (
                      <button key={i} onMouseDown={() => go(r.to)}
                        data-testid={`search-result-${i}`}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/60 text-left">
                        <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-muted text-muted-foreground min-w-[62px] text-center">{r.type}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{r.label}</div>
                          {r.sub && <div className="text-[11px] text-muted-foreground truncate">{r.sub}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            data-testid="ask-ai-btn"
            onClick={() => setAskAiOpen(true)}
            className="hidden md:flex items-center gap-1.5 h-9 px-3 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary transition-colors"
            aria-label="Ask AI"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Ask AI</span>
          </button>
          <button
            data-testid="ask-ai-btn-mobile"
            onClick={() => setAskAiOpen(true)}
            className="md:hidden w-9 h-9 rounded-lg hover:bg-muted/60 flex items-center justify-center text-primary"
            aria-label="Ask AI"
          >
            <Sparkles className="w-4 h-4" />
          </button>

          <button data-testid="theme-toggle" onClick={toggle} className="w-9 h-9 rounded-lg hover:bg-muted/60 flex items-center justify-center transition-colors" aria-label="theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Functional Notifications */}
          <Popover open={notifOpen} onOpenChange={setNotifOpen}>
            <PopoverTrigger asChild>
              <button data-testid="notifications-btn" className="relative w-9 h-9 rounded-lg hover:bg-muted/60 flex items-center justify-center transition-colors" aria-label="notifications">
                <Bell className="w-4 h-4" />
                {totalNotifs > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-semibold flex items-center justify-center">{totalNotifs}</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[360px] p-0" data-testid="notifications-panel">
              <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
                <div className="font-display text-sm font-semibold">Notifications</div>
                <span className="text-[10px] text-muted-foreground uppercase tracking-[0.15em]">{totalNotifs} unread</span>
              </div>
              <div className="max-h-[400px] overflow-auto">
                {notifs.low_stock.length === 0 && notifs.overdue.length === 0 && notifs.pending.length === 0 && (
                  <div className="p-6 text-sm text-muted-foreground text-center">All caught up ✨</div>
                )}
                {notifs.overdue.length > 0 && (
                  <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-destructive bg-destructive/5">Overdue Invoices</div>
                )}
                {notifs.overdue.map(i => (
                  <button key={i.id} onMouseDown={() => { setNotifOpen(false); nav("/invoices"); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 text-left border-b border-border/40">
                    <Clock className="w-4 h-4 text-destructive shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">Invoice {i.number} overdue</div>
                      <div className="text-[11px] text-muted-foreground">Due {i.due_date || "—"} · {fmt(i.total)}</div>
                    </div>
                  </button>
                ))}
                {notifs.low_stock.length > 0 && (
                  <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-primary bg-primary/5">Low Stock</div>
                )}
                {notifs.low_stock.map(i => (
                  <button key={i.id} onMouseDown={() => { setNotifOpen(false); nav("/inventory"); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 text-left border-b border-border/40">
                    <AlertTriangle className="w-4 h-4 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{i.name}</div>
                      <div className="text-[11px] text-muted-foreground">{i.quantity} {i.unit} left · reorder at {i.reorder_level}</div>
                    </div>
                  </button>
                ))}
                {notifs.pending.length > 0 && (
                  <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground bg-muted/40">Pending Payments</div>
                )}
                {notifs.pending.slice(0,4).map(i => (
                  <button key={i.id} onMouseDown={() => { setNotifOpen(false); nav("/invoices"); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 text-left border-b border-border/40">
                    <Receipt className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">Invoice {i.number}</div>
                      <div className="text-[11px] text-muted-foreground">Due {i.due_date || "—"} · {fmt(i.total)}</div>
                    </div>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </header>

        <main data-testid="main-content" className="p-4 lg:p-8 max-w-[1600px] mx-auto">
          {children}
        </main>
      </div>

      <AskAIDrawer open={askAiOpen} onClose={() => setAskAiOpen(false)} />
    </div>
  );
}
